/**
 * Radio resource API — filterable/sortable list, ICCID batch resolution,
 * detail lookup, and action dispatch.
 *
 * Backs the desktop RadioGrid (paginated server list), the mobile on-site
 * search / BLE resolution flow, and the radio detail panel + action dispatch
 * surface (sync / kore-sync / refresh).
 *
 * Scoping is enforced server-side: non-staff users only see radios attached
 * to their own sites. Staff-only fields (ip_address, online) are redacted in
 * non-staff responses.
 *
 * Contains:
 * - listRadios          — GET  /api/odl-app/radios/ with grid query params
 * - resolveRadios       — POST /api/odl-app/radios/resolve/ for ICCID batch lookup
 * - fetchRadio          — GET  /api/odl-app/radios/<id>/ for full detail
 * - fetchRadioActions   — GET  /api/odl-app/radios/<id>/actions/ for action catalog
 * - dispatchRadioActions — POST /api/odl-app/radios/<id>/actions/ to dispatch actions
 */

import apiClient from '@sophocles/http-common';

const RADIOS_BASE = 'odl-app/radios/';
const RADIOS_RESOLVE = 'odl-app/radios/resolve/';

/**
 * List radios with JSON:API-style filtering and sorting.
 *
 * @param {Object} params - Query params. Expects `sort` (comma-separated,
 *   leading `-` for desc), `filter[<field>]` keys, `offset`, `limit`.
 * @returns {Promise<{ payload: { results: Array, count: number } }>}
 */
export const listRadios = params => apiClient.get(RADIOS_BASE, { params }).then(r => r.data);

/**
 * Resolve a batch of ICCIDs to one answer row per identifier asked about.
 *
 * Supports tolerant suffix matching for the stripped form advertised over
 * BLE, on either side of the comparison. An identifier the requester's scope
 * cannot see comes back as `radio: null` rather than being omitted, so the
 * returned array is always the same length as `iccids`.
 *
 * @param {string[]} iccids - Full or partial ICCIDs (BLE-advertised suffix form accepted).
 * @returns {Promise<Array<{
 *   iccid: string,
 *   radio: { type: string, id: number, name: string } | null,
 *   valid?: boolean,
 *   creatable?: boolean
 * }>>} The unwrapped array — not the `{data}` envelope the response body carries.
 */
export const resolveRadios = iccids => apiClient.post(RADIOS_RESOLVE, { iccids }).then(r => r.data.data);

/**
 * Fetch full detail for a single radio.
 *
 * GET /api/odl-app/radios/<id>/
 * Returns the extended radio object including billing, kore_data, kore_usage,
 * and carrier. Outside-of-scope radios resolve as 404.
 *
 * @param {number|string} id
 * @returns {Promise<object>}
 */
export const fetchRadio = id => apiClient.get(`${RADIOS_BASE}${id}/`).then(r => r.data.data);
